import { useState, useEffect, useCallback } from 'react';
import {
  ClipboardCheck,
  Check,
  X,
  ExternalLink,
  Clock,
  User,
  Users,
  Mail,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { useI18n } from '@/i18n';
import { formatDateTime } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  getReviews,
  getReview,
  approveReview,
  rejectReview,
  type ListReviewsParams,
} from '@/api/reviews';
import type { AppRequestData } from '@/api/apps';

export default function ReviewsPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [reviews, setReviews] = useState<AppRequestData[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Filter and modal states
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [selectedRequest, setSelectedRequest] = useState<AppRequestData | null>(null);
  const [reviewNote, setReviewNote] = useState('');

  // Counts for tabs
  const [counts, setCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    all: 0,
  });

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      const params: ListReviewsParams = {
        page,
        pageSize,
      };
      if (filter !== 'all') {
        params.status = filter;
      }

      const result = await getReviews(params);
      setReviews(result?.items ?? []);
      setTotal(result?.total ?? 0);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load reviews';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filter, toast, t.common.error]);

  // Fetch counts for tabs
  const fetchCounts = useCallback(async () => {
    try {
      const [pendingRes, approvedRes, rejectedRes, allRes] = await Promise.all([
        getReviews({ status: 'pending', pageSize: 1 }),
        getReviews({ status: 'approved', pageSize: 1 }),
        getReviews({ status: 'rejected', pageSize: 1 }),
        getReviews({ pageSize: 1 }),
      ]);
      setCounts({
        pending: pendingRes?.total ?? 0,
        approved: approvedRes?.total ?? 0,
        rejected: rejectedRes?.total ?? 0,
        all: allRes?.total ?? 0,
      });
    } catch {
      // Silently fail, counts will just show 0
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // Handle approve
  const handleApprove = async (request: AppRequestData) => {
    try {
      setSubmitting(true);
      await approveReview(request.id, { note: reviewNote || undefined });
      toast({
        title: t.common.success,
        description: t.admin.requestApproved,
      });
      setSelectedRequest(null);
      setReviewNote('');
      fetchReviews();
      fetchCounts();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle reject
  const handleReject = async (request: AppRequestData) => {
    if (!reviewNote.trim()) {
      toast({
        title: t.common.error,
        description: t.admin.reviewNoteRequired,
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);
      await rejectReview(request.id, { note: reviewNote });
      toast({
        title: t.common.success,
        description: t.admin.requestRejected,
      });
      setSelectedRequest(null);
      setReviewNote('');
      fetchReviews();
      fetchCounts();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle view request detail
  const handleViewRequest = async (id: string) => {
    try {
      const detail = await getReview(id);
      setSelectedRequest(detail);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load detail';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    }
  };

  const tabs = [
    { key: 'pending', label: t.status.pending, count: counts.pending },
    { key: 'approved', label: t.status.approved, count: counts.approved },
    { key: 'rejected', label: t.status.rejected, count: counts.rejected },
    { key: 'all', label: t.common.all, count: counts.all },
  ];

  const totalPages = Math.ceil(total / pageSize);

  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t.admin.appReview}</h1>
          <p className="text-gray-500 mt-1">
            {t.admin.appReviewDesc}
            {counts.pending > 0 && (
              <span className="ml-2 text-yellow-600">{t.admin.pendingCount.replace('{count}', String(counts.pending))}</span>
            )}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as typeof filter)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              filter === tab.key
                ? 'bg-primary-100 text-primary-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
            <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-gray-200 rounded-full">
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Request list */}
      <div className="space-y-4">
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">
                {filter === 'pending' ? t.admin.noReviews : t.admin.noReviewsDesc}
              </p>
            </CardContent>
          </Card>
        ) : (
          reviews.map((request) => (
            <Card key={request.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.appName}
                      </h3>
                      <Badge
                        variant={
                          request.type === 'create'
                            ? 'info'
                            : request.type === 'update'
                            ? 'warning'
                            : 'danger'
                        }
                      >
                        {t.requestTypes[request.type as keyof typeof t.requestTypes]}
                      </Badge>
                      <Badge
                        variant={
                          request.status === 'approved'
                            ? 'success'
                            : request.status === 'rejected'
                            ? 'danger'
                            : 'warning'
                        }
                      >
                        {t.status[request.status as keyof typeof t.status]}
                      </Badge>
                    </div>

                    <p className="text-gray-600 mb-3">{request.appDescription}</p>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {request.creatorEmail}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDateTime(request.createdAt)}
                      </span>
                      {request.appLink && (
                        <a
                          href={request.appLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary-600 hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t.admin.viewApp}
                        </a>
                      )}
                    </div>

                    {/* Permissions */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {request.allowedGroups?.map((group) => (
                        <span
                          key={group}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                        >
                          <Users className="w-3 h-3" />
                          {group}
                        </span>
                      ))}
                      {request.allowedEmails?.map((email) => (
                        <span
                          key={email}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded-full"
                        >
                          <Mail className="w-3 h-3" />
                          {email}
                        </span>
                      ))}
                    </div>

                    {/* Category tags */}
                    {request.categoryTags && request.categoryTags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {request.categoryTags.map((tag) => (
                          <Badge key={tag} variant="default">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Review note */}
                    {request.reviewNote && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">{t.creator.reviewNoteLabel}</span>
                          {request.reviewNote}
                        </p>
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && (
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleViewRequest(request.id)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {t.admin.reviewApp}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-gray-500">
            {t.admin.showingReviews
              .replace('{from}', String((page - 1) * pageSize + 1))
              .replace('{to}', String(Math.min(page * pageSize, total)))
              .replace('{total}', String(total))}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-700">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Review Modal */}
      <Modal
        isOpen={selectedRequest !== null}
        onClose={() => {
          setSelectedRequest(null);
          setReviewNote('');
        }}
        title={t.admin.reviewTitle}
        description={selectedRequest ? t.admin.reviewDesc.replace('{name}', selectedRequest.appName || '').replace('{type}', t.requestTypes[selectedRequest.type as keyof typeof t.requestTypes]) : ''}
        size="lg"
      >
        {selectedRequest && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div>
                <span className="text-sm text-gray-500">{t.admin.appName}:</span>
                <span className="text-gray-900 ml-2">{selectedRequest.appName}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.admin.appLink}:</span>
                <a
                  href={selectedRequest.appLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline ml-2"
                >
                  {selectedRequest.appLink}
                </a>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.common.description}:</span>
                <p className="text-gray-900 mt-1">{selectedRequest.appDescription}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">{t.admin.visibleScope}:</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {selectedRequest.allowedGroups?.map((group) => (
                    <Badge key={group} variant="info">{group}</Badge>
                  ))}
                  {selectedRequest.allowedEmails?.map((email) => (
                    <Badge key={email} variant="success">{email}</Badge>
                  ))}
                </div>
              </div>
            </div>

            <Textarea
              label={t.admin.reviewNote}
              placeholder={t.admin.reviewNotePlaceholder}
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              rows={3}
            />

            <ModalFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedRequest(null);
                  setReviewNote('');
                }}
                disabled={submitting}
              >
                {t.common.cancel}
              </Button>
              <Button
                variant="danger"
                onClick={() => handleReject(selectedRequest)}
                disabled={submitting}
              >
                {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                <X className="w-4 h-4 mr-1" />
                {t.admin.reject}
              </Button>
              <Button
                variant="success"
                onClick={() => handleApprove(selectedRequest)}
                disabled={submitting}
              >
                {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                <Check className="w-4 h-4 mr-1" />
                {t.admin.approve}
              </Button>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </div>
  );
}
